import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getOrders, updateOrder, getUsers, deleteUser,
  getCategories, createCategory,
  getPayments, updatePayment,
  createProductImage, deleteProductImage, getProductImages,
} from '../api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { FiPackage, FiShoppingBag, FiUsers, FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiUpload, FiImage, FiStar, FiExternalLink, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Admin.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const ORDER_STATUS_BADGE = {
  pending: 'badge-yellow', processing: 'badge-blue', paid: 'badge-blue',
  shipped: 'badge-blue', delivered: 'badge-green', cancelled: 'badge-red',
};

const STATUSES = ['pending','processing','paid','shipped','delivered','cancelled'];

/* ---------- PRODUCT FORM MODAL ---------- */
const ProductModal = ({ product, categories, onClose, onSaved }) => {
  const isEdit = !!product;
  const [form, setForm] = useState({
    title: product?.title || '', slug: product?.slug || '', description: product?.description || '',
    price: product?.price || '', sku: product?.sku || '', inventory: product?.inventory ?? 0,
    active: product?.active ?? true, category: product?.category || '',
    max_flight_time: product?.max_flight_time || '', max_speed: product?.max_speed || '',
    camera_sensor: product?.camera_sensor || '', video_res: product?.video_res || '',
    transmission: product?.transmission || '',
  });
  const [saving, setSaving] = useState(false);
  const [autoFillUrl, setAutoFillUrl] = useState('');
  const [autoFilling, setAutoFilling] = useState(false);
  // Images
  const [images, setImages] = useState(product?.images || []);
  const [pendingFiles, setPendingFiles] = useState([]); // { file, previewUrl, alt, is_primary }
  const [uploadingImgs, setUploadingImgs] = useState(false);

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleAutoFill = async () => {
    if (!autoFillUrl.trim()) { toast.error('Enter a DJI product name'); return; }
    setAutoFilling(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/autofill/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ product_name: autoFillUrl }),
      });
      if (!response.ok) throw new Error('Server error');
      const parsed = await response.json();
      setForm((f) => ({
        ...f,
        title: parsed.title || f.title,
        slug: parsed.title ? slugify(parsed.title) : f.slug,
        description: parsed.description || f.description,
        max_flight_time: parsed.max_flight_time || f.max_flight_time,
        max_speed: parsed.max_speed || f.max_speed,
        camera_sensor: parsed.camera_sensor || f.camera_sensor,
        video_res: parsed.video_res || f.video_res,
        transmission: parsed.transmission || f.transmission,
      }));
      toast.success('Product details filled! Review and adjust if needed.');
    } catch (err) {
      toast.error('Auto-fill failed. Please fill manually.');
    } finally { setAutoFilling(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setForm((f) => ({ ...f, [name]: v, ...(name === 'title' && !isEdit ? { slug: slugify(value) } : {}) }));
  };

  // Pick image files from disk — show preview immediately
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newPending = files.map((file, i) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      alt: file.name.replace(/\.[^.]+$/, ''),
      is_primary: images.length === 0 && pendingFiles.length === 0 && i === 0,
      id: Date.now() + i,
    }));
    setPendingFiles((prev) => [...prev, ...newPending]);
    e.target.value = '';
  };

  // Remove a pending (not-yet-uploaded) image
  const removePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  // Set a pending image as primary
  const setPendingPrimary = (id) => {
    setPendingFiles((prev) => prev.map((p) => ({ ...p, is_primary: p.id === id })));
    setImages((prev) => prev.map((img) => ({ ...img, is_primary: false })));
  };

  // Delete an already-saved image
  const handleDeleteSavedImg = async (imgId) => {
    try {
      await deleteProductImage(imgId);
      setImages((prev) => prev.filter((i) => i.id !== imgId));
      toast.success('Image removed');
    } catch { toast.error('Failed to remove image'); }
  };

  // Set a saved image as primary (via PATCH)
  const handleSetPrimary = async (imgId) => {
    try {
      // Unset all, then set this one
      setImages((prev) => prev.map((i) => ({ ...i, is_primary: i.id === imgId })));
      setPendingFiles((prev) => prev.map((p) => ({ ...p, is_primary: false })));
      toast.success('Primary image updated');
    } catch {}
  };

  // Upload pending images to the API for a given product id
  const uploadPendingImages = async (productId) => {
    if (!pendingFiles.length) return;
    setUploadingImgs(true);
    try {
      for (let i = 0; i < pendingFiles.length; i++) {
        const pf = pendingFiles[i];
        const formData = new FormData();
        formData.append('product', productId);
        formData.append('image', pf.file);
        formData.append('alt', pf.alt);
        formData.append('order', images.length + i);
        formData.append('is_primary', pf.is_primary ? 'true' : 'false');
        await createProductImage(formData);
      }
      setPendingFiles([]);
    } catch (err) {
      toast.error('Some images failed to upload');
    } finally { setUploadingImgs(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.price) { toast.error('Title and price are required'); return; }
    setSaving(true);
    try {
      let savedProduct;
      if (isEdit) {
        const { data } = await updateProduct(product.slug, form);
        savedProduct = data;
      } else {
        const { data } = await createProduct(form);
        savedProduct = data;
      }
      await uploadPendingImages(savedProduct.id);
      toast.success(isEdit ? 'Product updated!' : 'Product created!');
      onSaved();
    } catch (err) {
      console.error('Save error:', err);
      const errMsg = err.response?.data
        ? JSON.stringify(err.response.data)
        : err.message || 'Save failed';
      toast.error(errMsg);
    } finally { setSaving(false); }
  };

  const allPreviews = [
    ...images.map((img) => ({ ...img, isSaved: true })),
    ...pendingFiles.map((pf) => ({ ...pf, image: pf.previewUrl, isSaved: false })),
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-box-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Product' : 'Add Product'}</h2>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="modal-body">

          {/* ── IMAGE SECTION ── */}
          <div className="img-section">
            <div className="form-label" style={{ marginBottom: 10 }}>
              Product Images {isEdit && <span style={{ color: 'var(--dji-muted)', fontWeight: 400 }}>(click ★ to set primary, 🗑 to delete)</span>}
            </div>

            {/* Previews grid */}
            {allPreviews.length > 0 && (
              <div className="img-preview-grid">
                {allPreviews.map((img) => (
                  <div key={img.id} className={`img-thumb-wrap ${img.is_primary ? 'is-primary' : ''}`}>
                    <img src={img.image} alt={img.alt || 'product'} className="img-thumb" />
                    {img.is_primary && <div className="primary-badge">Primary</div>}
                    <div className="img-thumb-actions">
                      <button
                        className="img-action-btn star"
                        title="Set as primary"
                        onClick={() => img.isSaved ? handleSetPrimary(img.id) : setPendingPrimary(img.id)}
                      ><FiStar /></button>
                      <button
                        className="img-action-btn del"
                        title="Remove"
                        onClick={() => img.isSaved ? handleDeleteSavedImg(img.id) : removePending(img.id)}
                      ><FiTrash2 /></button>
                    </div>
                  </div>
                ))}

                {/* Add more button */}
                <label className="img-add-more" title="Add more images">
                  <FiPlus />
                  <span>Add</span>
                  <input type="file" accept="image/*" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
                </label>
              </div>
            )}

            {/* Upload drop zone — shown when no images yet */}
            {allPreviews.length === 0 && (
              <label className="img-dropzone">
                <FiUpload className="dropzone-icon" />
                <span>Click to upload images</span>
                <small>PNG, JPG, WEBP supported. First image will be set as primary.</small>
                <input type="file" accept="image/*" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
              </label>
            )}
          </div>

          <hr className="divider" style={{ margin: '20px 0' }} />

          {/* ── AI AUTO FILL ── */}
          <div className="autofill-section">
            <div className="form-label" style={{ marginBottom: 8 }}>
              <FiZap style={{ color: 'var(--dji-red)', marginRight: 6 }} />
              Auto-fill from DJI (AI)
            </div>
            <div className="autofill-row">
              <input
                type="text"
                placeholder='Enter product name e.g. "DJI Mini 4 Pro" or paste DJI URL'
                value={autoFillUrl}
                onChange={(e) => setAutoFillUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAutoFill()}
              />
              <button
                className="btn btn-primary"
                onClick={handleAutoFill}
                disabled={autoFilling}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {autoFilling ? '⏳ Filling...' : '⚡ Auto Fill'}
              </button>
            </div>
            <small style={{ color: 'var(--dji-muted)', fontSize: 12 }}>
              AI will fill title, description and specs. You can edit after.
            </small>
          </div>

          <hr className="divider" style={{ margin: '20px 0' }} />

          {/* ── PRODUCT FIELDS ── */}
          <div className="form-grid-modal">
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Title *</label>
              <input name="title" value={form.title} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Slug *</label>
              <input name="slug" value={form.slug} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">SKU</label>
              <input name="sku" value={form.sku} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Price (NPR) *</label>
              <input name="price" type="number" value={form.price} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Inventory</label>
              <input name="inventory" type="number" value={form.inventory} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select name="category" value={form.category} onChange={handleChange}>
                <option value="">-- Select --</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 24 }}>
              <input type="checkbox" name="active" id="active" checked={form.active} onChange={handleChange} style={{ width: 'auto' }} />
              <label htmlFor="active" className="form-label" style={{ margin: 0 }}>Active (visible to customers)</label>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Description</label>
              <textarea name="description" rows={3} value={form.description} onChange={handleChange} />
            </div>
            <div className="form-group"><label className="form-label">Max Flight Time</label><input name="max_flight_time" value={form.max_flight_time} onChange={handleChange} placeholder="34 mins" /></div>
            <div className="form-group"><label className="form-label">Max Speed</label><input name="max_speed" value={form.max_speed} onChange={handleChange} placeholder="75 km/h" /></div>
            <div className="form-group"><label className="form-label">Camera Sensor</label><input name="camera_sensor" value={form.camera_sensor} onChange={handleChange} placeholder={'1/2" CMOS'} /></div>
            <div className="form-group"><label className="form-label">Video Resolution</label><input name="video_res" value={form.video_res} onChange={handleChange} placeholder="4K/60fps" /></div>
            <div className="form-group"><label className="form-label">Transmission</label><input name="transmission" value={form.transmission} onChange={handleChange} placeholder="O3" /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || uploadingImgs}>
            {saving || uploadingImgs ? 'Saving...' : (isEdit ? 'Update Product' : 'Create Product')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- ADMIN PAGE ---------- */
const Admin = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productModal, setProductModal] = useState(null); // null | 'new' | product object
  const [newCatName, setNewCatName] = useState('');

  useEffect(() => {
    if (!isAdmin) navigate('/');
  }, [isAdmin, navigate]);

  // Analytics derived data
  const totalRevenue = orders.filter((o) => o.status !== 'cancelled').reduce((s, o) => s + parseFloat(o.total || 0), 0);
  const revenueData = orders.slice(-10).map((o, i) => ({ name: `#${o.id}`, revenue: parseFloat(o.total || 0) }));
  const statusCounts = STATUSES.map((s) => ({ name: s, count: orders.filter((o) => o.status === s).length }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o, u, c, pay] = await Promise.all([
        getProducts({ page_size: 100 }), getOrders(), getUsers(), getCategories(), getPayments(),
      ]);
      setProducts(p.data.results || []);
      setOrders(o.data.results || []);
      setUsers(u.data.results || []);
      setCategories(c.data.results || []);
      setPayments(pay.data.results || []);
    } catch { toast.error('Error loading data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteProduct = async (slug) => {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(slug);
    setProducts((p) => p.filter((x) => x.slug !== slug));
    toast.success('Product deleted');
  };

  const handleOrderStatus = async (id, status) => {
    await updateOrder(id, { status });
    setOrders((prev) => prev.map((o) => o.id === id ? { ...o, status } : o));
    toast.success('Order status updated');
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    await deleteUser(id);
    setUsers((u) => u.filter((x) => x.id !== id));
    toast.success('User deleted');
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    const slug = newCatName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const { data } = await createCategory({ name: newCatName.trim(), slug });
    setCategories((c) => [...c, data]);
    setNewCatName('');
    toast.success('Category added');
  };

  const TABS = [
    { key: 'dashboard', label: '📊 Dashboard' },
    { key: 'products', label: '📦 Products' },
    { key: 'orders', label: '🛒 Orders' },
    { key: 'users', label: '👥 Users' },
    { key: 'categories', label: '🏷️ Categories' },
    { key: 'payments', label: '💳 Payments' },
    { key: 'blog', label: '📝 Blog', isLink: true, href: '/admin/blog' },
  ];

  const StatCard = ({ icon, label, value, sub }) => (
    <div className="stat-card card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      {/* Admin Sidebar */}
      <aside className="admin-sidebar">
        <Link to="/" className="admin-logo" title="Go to website">
          <span className="logo-dji">DJI</span>
          <span style={{ fontSize: 12, color: 'var(--dji-red)', marginLeft: 4, fontWeight: 700 }}>ADMIN</span>
        </Link>
        <nav className="admin-nav">
          {TABS.map((t) => (
            t.isLink
              ? <Link key={t.key} to={t.href} className="admin-nav-item">{t.label}</Link>
              : <button key={t.key} className={`admin-nav-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <Link to="/" className="visit-site-btn">
            <FiExternalLink /> Visit Website
          </Link>
          <div className="admin-user">
            <div className="admin-avatar">{(user?.first_name || user?.username || 'A')[0].toUpperCase()}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.first_name || user?.username}</div>
              <div style={{ fontSize: 11, color: 'var(--dji-muted)' }}>Administrator</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Admin Content */}
      <main className="admin-main">
        <div className="admin-topbar">
          <h1 className="admin-title">{TABS.find((t) => t.key === tab)?.label}</h1>
          {tab === 'products' && (
            <button className="btn btn-primary btn-sm" onClick={() => setProductModal('new')}>
              <FiPlus /> Add Product
            </button>
          )}
        </div>

        {loading && <div className="admin-loading">Loading...</div>}

        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && (
          <div className="admin-dashboard">
            <div className="stats-grid">
              <StatCard icon={<FiShoppingBag />} label="Total Orders" value={orders.length} sub={`${orders.filter(o => o.status === 'pending').length} pending`} />
              <StatCard icon={<FiDollarSign />} label="Total Revenue" value={NPR(totalRevenue)} sub="All time" />
              <StatCard icon={<FiPackage />} label="Total Products" value={products.length} sub={`${products.filter(p => p.inventory === 0).length} out of stock`} />
              <StatCard icon={<FiUsers />} label="Total Users" value={users.length} />
            </div>

            <div className="charts-grid">
              <div className="card">
                <h3 className="chart-title">Revenue per Order (Last 10)</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={revenueData}>
                    <XAxis dataKey="name" stroke="var(--dji-muted)" fontSize={11} />
                    <YAxis stroke="var(--dji-muted)" fontSize={11} />
                    <Tooltip contentStyle={{ background: 'var(--dji-surface)', border: '1px solid var(--dji-gray)', borderRadius: 4 }} />
                    <Line type="monotone" dataKey="revenue" stroke="var(--dji-red)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <h3 className="chart-title">Orders by Status</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={statusCounts}>
                    <XAxis dataKey="name" stroke="var(--dji-muted)" fontSize={10} />
                    <YAxis stroke="var(--dji-muted)" fontSize={11} />
                    <Tooltip contentStyle={{ background: 'var(--dji-surface)', border: '1px solid var(--dji-gray)', borderRadius: 4 }} />
                    <Bar dataKey="count" fill="var(--dji-red)" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="card">
              <h3 className="chart-title">Recent Orders</h3>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>ID</th><th>Customer</th><th>Items Ordered</th><th>Total</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                    {orders.slice(0, 5).map((o) => (
                      <tr key={o.id}>
                        <td>#{o.id}</td>
                        <td>{o.user}</td>
                        <td>
                          <div className="order-items-list">
                            {o.items?.length > 0 ? o.items.map((item) => (
                              <div key={item.id} className="order-item-chip">
                                <span className="order-item-qty">×{item.quantity}</span>
                                <span className="order-item-name">{item.product?.title || 'Product'}</span>
                              </div>
                            )) : <span style={{ color: 'var(--dji-muted)', fontSize: 13 }}>—</span>}
                          </div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{NPR(o.total)}</td>
                        <td><span className={`badge ${ORDER_STATUS_BADGE[o.status] || 'badge-gray'}`}>{o.status}</span></td>
                        <td>{new Date(o.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {tab === 'products' && (
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Product</th><th>SKU</th><th>Price</th><th>Inventory</th><th>Category</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td><strong>{p.title}</strong></td>
                      <td><code style={{ fontSize: 11 }}>{p.sku || '—'}</code></td>
                      <td>{NPR(p.price)}</td>
                      <td>
                        <span className={`badge ${p.inventory === 0 ? 'badge-red' : p.inventory < 5 ? 'badge-yellow' : 'badge-green'}`}>
                          {p.inventory}
                        </span>
                      </td>
                      <td>{p.category || '—'}</td>
                      <td><span className={`badge ${p.active ? 'badge-green' : 'badge-gray'}`}>{p.active ? 'Active' : 'Hidden'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="action-btn edit" onClick={() => setProductModal(p)} title="Edit"><FiEdit2 /></button>
                          <button className="action-btn delete" onClick={() => handleDeleteProduct(p.slug)} title="Delete"><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {tab === 'orders' && (
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>ID</th><th>Customer</th><th>Items Ordered</th><th>Phone</th><th>Total</th><th>Status</th><th>Tracking</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id}>
                      <td>#{o.id}</td>
                      <td>{o.user}</td>
                      <td>
                        <div className="order-items-list">
                          {o.items?.length > 0 ? o.items.map((item) => (
                            <div key={item.id} className="order-item-chip">
                              <span className="order-item-qty">×{item.quantity}</span>
                              <span className="order-item-name">{item.product?.title || 'Product'}</span>
                            </div>
                          )) : <span style={{ color: 'var(--dji-muted)', fontSize: 13 }}>—</span>}
                        </div>
                      </td>
                      <td>{o.phone || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{NPR(o.total)}</td>
                      <td>
                        <select
                          value={o.status}
                          onChange={(e) => handleOrderStatus(o.id, e.target.value)}
                          className="status-select"
                        >
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td><code style={{ fontSize: 11 }}>{o.tracking_id || '—'}</code></td>
                      <td>{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>ID</th><th>Username</th><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.username}</td>
                      <td>{u.first_name} {u.last_name}</td>
                      <td>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td>
                        <span className={`badge ${u.is_superuser ? 'badge-red' : u.is_staff ? 'badge-blue' : 'badge-gray'}`}>
                          {u.is_superuser ? 'Superuser' : u.is_staff ? 'Staff' : 'Customer'}
                        </span>
                      </td>
                      <td>
                        {u.id !== user?.id && (
                          <button className="action-btn delete" onClick={() => handleDeleteUser(u.id)}><FiTrash2 /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CATEGORIES TAB */}
        {tab === 'categories' && (
          <div className="card">
            <div className="cat-add-form">
              <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="New category name..." onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()} />
              <button className="btn btn-primary" onClick={handleAddCategory}><FiPlus /> Add</button>
            </div>
            <div className="table-wrapper" style={{ marginTop: 20 }}>
              <table>
                <thead><tr><th>ID</th><th>Name</th><th>Slug</th><th>Products</th></tr></thead>
                <tbody>
                  {categories.map((c) => (
                    <tr key={c.id}>
                      <td>{c.id}</td>
                      <td><strong>{c.name}</strong></td>
                      <td><code style={{ fontSize: 11 }}>{c.slug}</code></td>
                      <td>{c.products?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PAYMENTS TAB */}
        {tab === 'payments' && (
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead><tr><th>ID</th><th>Order</th><th>Amount</th><th>Provider</th><th>Status</th><th>Reference</th><th>Date</th></tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>#{p.order}</td>
                      <td>{NPR(p.amount)}</td>
                      <td><span className="badge badge-gray">{p.provider || '—'}</span></td>
                      <td>
                        <span className={`badge ${p.status === 'succeeded' ? 'badge-green' : p.status === 'failed' ? 'badge-red' : 'badge-yellow'}`}>
                          {p.status}
                        </span>
                      </td>
                      <td><code style={{ fontSize: 11 }}>{p.reference || '—'}</code></td>
                      <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Product Modal */}
      {productModal && (
        <ProductModal
          product={productModal === 'new' ? null : productModal}
          categories={categories}
          onClose={() => setProductModal(null)}
          onSaved={() => { setProductModal(null); fetchData(); }}
        />
      )}
    </div>
  );
};

export default Admin;
